import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useProvider } from "@/hooks/useProvider";
import { supabase } from "@/lib/supabase";
import { fetchWithdrawals, fetchSettlements } from "@/lib/data";
import { withdrawals as mockWithdrawals } from "@/mocks/withdrawals";
import { settlements as mockSettlements } from "@/mocks/settlements";
import type { Withdrawal } from "@/mocks/withdrawals";
import type { Settlement } from "@/mocks/settlements";

function parseAmount(str: string): number {
  return Number(str.replace(/,/g, ""));
}

function formatAmount(n: number): string {
  return n.toLocaleString("en-US");
}

const MIN_WITHDRAWAL = 100;

export default function WithdrawPage() {
  const navigate = useNavigate();
  const { provider, loading: providerLoading } = useProvider();

  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);

  const [wAmount, setWAmount] = useState("");
  const [wSubmitting, setWSubmitting] = useState(false);
  const [wSuccess, setWSuccess] = useState(false);
  const [wError, setWError] = useState("");

  useEffect(() => {
    setLoading(true);
    if (!provider) {
      setSettlements(mockSettlements);
      setWithdrawals(mockWithdrawals);
      setLoading(false);
      return;
    }
    Promise.all([fetchSettlements(provider.id), fetchWithdrawals(provider.id)])
      .then(([s, w]) => {
        setSettlements(s);
        setWithdrawals(w);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [provider]);

  const available = settlements
    .filter((s) => s.status === "review")
    .reduce((sum, s) => sum + parseAmount(s.net), 0);

  const maxWithdrawal = available;

  const handleWithdraw = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setWError("");

    const formData = new FormData(e.currentTarget);
    if ((formData.get("company_alt") as string | null)?.trim()) {
      setWSuccess(true);
      setWAmount("");
      setTimeout(() => setWSuccess(false), 4000);
      return;
    }

    const amountVal = parseAmount(wAmount);
    if (Number.isNaN(amountVal) || amountVal <= 0) {
      setWError("أدخل مبلغ صحيح.");
      return;
    }
    if (amountVal < MIN_WITHDRAWAL) {
      setWError(`الحد الأدنى للسحب ${formatAmount(MIN_WITHDRAWAL)} ر.س`);
      return;
    }
    if (amountVal > maxWithdrawal) {
      setWError(`الحد الأقصى للسحب ${formatAmount(maxWithdrawal)} ر.س`);
      return;
    }

    setWSubmitting(true);

    if (!provider) {
      setTimeout(() => {
        setWithdrawals((prev) => [
          {
            id: Date.now(),
            amount: formatAmount(amountVal),
            status: "pending",
            statusLabel: "قيد المراجعة",
            bankName: "مصرف الراجحي",
            iban: "SA44 2000 0001 2345 6789 1234",
            accountName: "شركة الماية للخدمات",
            date: "اليوم",
          },
          ...prev,
        ]);
        setWSubmitting(false);
        setWSuccess(true);
        setWAmount("");
        setTimeout(() => setWSuccess(false), 4000);
      }, 800);
      return;
    }

    const { error: insertErr } = await supabase.from("withdrawals").insert({
      provider_id: provider.id,
      amount: amountVal,
      status: "pending",
      bank_name: provider?.bank_name || "مصرف الراجحي",
      iban: provider?.bank_iban || "",
      account_name: provider?.bank_account_name || "",
    });

    if (insertErr) {
      setWSubmitting(false);
      setWError("تعذر إرسال طلب السحب، حاول مرة أخرى.");
      return;
    }

    fetchWithdrawals(provider.id)
      .then(setWithdrawals)
      .catch(() => {});

    setWSubmitting(false);
    setWSuccess(true);
    setWAmount("");
    setTimeout(() => setWSuccess(false), 4000);
  };

  const inputClass =
    "h-12 w-full rounded-[14px] border border-[#E4EFEF] bg-[#F0FDFD] px-4 text-sm text-ghasla-text-heading outline-none transition-colors focus:border-[#0D6E6E]";

  if (providerLoading || loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#E4EFEF] border-t-[#0D6E6E]" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[640px]">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate("/provider/settlements")}
        className="mb-5 inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-ghasla-text-secondary transition-colors hover:text-ghasla-text-link"
      >
        <i className="ri-arrow-right-line text-lg" />
        العودة للتسويات
      </button>

      <h1 className="text-[28px] font-bold text-ghasla-text-heading">سحب التسوية</h1>
      <p className="mt-1 text-sm text-ghasla-text-primary">
        الرصيد المتاح: <span className="font-semibold text-ghasla-text-link">{formatAmount(available)} ر.س</span>
      </p>

      {wSuccess ? (
        <div className="mt-6 flex flex-col items-center rounded-[20px] bg-white p-8 text-center shadow-card">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#E8F9F0] text-[#27AE60]">
            <i className="ri-checkbox-circle-line text-3xl" />
          </div>
          <p className="mt-4 text-lg font-semibold text-ghasla-text-heading">
            تم إرسال طلب السحب بنجاح
          </p>
          <p className="mt-1 text-sm text-ghasla-text-primary">
            فريق الإدارة بيراجع الطلب وبيحول المبلغ خلال 1-2 يوم عمل
          </p>
          <Link
            to="/provider/settlements"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            <i className="ri-arrow-right-line" />
            العودة للتسويات
          </Link>
        </div>
      ) : (
        <form onSubmit={handleWithdraw} className="mt-6 space-y-5 rounded-[20px] bg-white p-6 shadow-card md:p-8">
          {/* honeypot */}
          <div className="company-alt-field">
            <label htmlFor="company_alt">اسم الشركة (بديل)</label>
            <input
              id="company_alt"
              name="company_alt"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-ghasla-text-primary">
              المبلغ (ر.س)
            </label>
            <input
              type="number"
              min={MIN_WITHDRAWAL}
              max={maxWithdrawal}
              className={inputClass}
              value={wAmount}
              onChange={(e) => setWAmount(e.target.value)}
              placeholder={`الحد الأقصى: ${formatAmount(maxWithdrawal)} ر.س`}
            />
            <p className="mt-1.5 text-xs text-ghasla-text-secondary">
              الحد الأدنى {formatAmount(MIN_WITHDRAWAL)} ر.س · الحد الأقصى {formatAmount(maxWithdrawal)} ر.س
            </p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-ghasla-text-primary">
              الحساب البنكي
            </label>
            <div className="rounded-[14px] border border-[#E4EFEF] bg-[#F0FDFD] p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-ghasla-text-secondary">البنك</span>
                <span className="font-medium text-ghasla-text-heading">
                  {provider?.bank_name || "مصرف الراجحي"}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-ghasla-text-secondary">الآيبان</span>
                <span className="font-medium text-ghasla-text-heading" dir="ltr">
                  {provider?.bank_iban || "SA44 2000 0001 2345 6789 1234"}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-ghasla-text-secondary">اسم الحساب</span>
                <span className="font-medium text-ghasla-text-heading">
                  {provider?.bank_account_name || "شركة الماية للخدمات"}
                </span>
              </div>
            </div>
            <p className="mt-1.5 text-xs text-ghasla-text-secondary">
              لتغيير الحساب البنكي توجه لصفحة الإعدادات
            </p>
          </div>

          <div className="rounded-xl bg-[#FFF9EB] p-3">
            <p className="flex items-start gap-2 text-xs text-[#B45309]">
              <i className="ri-information-line mt-0.5 text-base" />
              <span>
                يستغرق التحويل 1-2 يوم عمل بعد الموافقة. الحد الأدنى للسحب {formatAmount(MIN_WITHDRAWAL)} ر.س.
              </span>
            </p>
          </div>

          {wError && (
            <p className="text-sm font-medium text-[#E74C3C]">{wError}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={wSubmitting || !wAmount}
              className="flex-1 cursor-pointer whitespace-nowrap rounded-xl bg-gradient-to-l from-[#00AAB7] to-[#1A9E96] px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {wSubmitting ? "جارٍ الإرسال..." : "تأكيد السحب"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/provider/settlements")}
              className="cursor-pointer whitespace-nowrap rounded-xl border border-[#E4EFEF] px-5 py-3 text-sm font-medium text-ghasla-text-primary transition-colors hover:bg-[#F0FDFD]"
            >
              إلغاء
            </button>
          </div>
        </form>
      )}
    </div>
  );
}